/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';
import {
  FormItem,
  InputField,
  Option,
  Select,
  Size,
  Stack,
  Form as VaiForm,
  Tooltip
} from '@vaisala/rockhopper-components';

import styled from 'styled-components';
import { useState, Dispatch, SetStateAction } from 'react';

const Form = styled(VaiForm)`
  .heading-container {
    display: flex;
    margin-bottom: 16px;
  }

  .profile-icon-container {
    position: relative;
    margin-right: 17px;
  }

  .profile-icon {
    font-size: 48px;
  }

  .heading {
    margin: 3px 0 0 0;
  }

  .vcup-table {
    display: block;
    margin-bottom: $vai-spacing-s;
  }

  .buttons-container {
    display: absolute;
    flex-direction: row;
    margin-right: 10px;
    margin-bottom: 10px;
  }

  .save-and-delete-buttons {
    position: absolute;
    width: 226px;
    margin-right: 10px;
    margin-bottom: 10px;
  }
`;

interface Props {
  displayErrors : boolean,
  nSplit : number,
  setNSplit : Dispatch<SetStateAction<number>>,
  maxDesync : number,
  setMaxDesync :Dispatch<SetStateAction<number>>,
  htFilter : number,
  setHtFilter : Dispatch<SetStateAction<number>>,
  nSigma : number[],
  setNSigma : Dispatch<SetStateAction<number[]>>,
  meFilter : number,
  setMEFilter : Dispatch<SetStateAction<number>>,
  valid : boolean,
  setValid : Dispatch<SetStateAction<boolean>>
}

export const ReconstructionParamForm: React.FC<Props> = (props) => {

  const {
    displayErrors,
    nSplit, setNSplit,
    maxDesync, setMaxDesync,
    htFilter, setHtFilter,
    nSigma, setNSigma,
    meFilter, setMEFilter,
    setValid
  } = props

  const [nSplitValid, setNSplitValid] = useState<boolean>(Number.isSafeInteger(nSplit) && nSplit > 0 && nSplit <= 600);
  const validateMaxDesync = (value: number): boolean => {
    return !isNaN(value) && value >= 0.1 && value <= 60.0;
  }
  const [maxDesyncValid, setMaxDesyncValid] = useState<boolean>(() => validateMaxDesync(maxDesync));
  const [htFilterValid, setHtFilterValid] = useState<boolean>([0, 1, 2, 3].includes(htFilter));
  const [nSigmaValid, setNSigmaValid] = useState<boolean>(nSigma[0] >= 0 && nSigma[1] >= 0);
  const [meFilterValid, setMEFilterValid] = useState<boolean>(meFilter >= 0 && meFilter <= 100);

  React.useEffect(() => {

    const new_valid = nSplitValid && maxDesyncValid && htFilterValid && nSigmaValid && meFilterValid;
    setValid(new_valid);
  }, [nSplitValid, maxDesyncValid, htFilterValid, nSigmaValid, meFilterValid])
  
  const validateNSplit = async () => {
    setNSplitValid(Number.isSafeInteger(nSplit) && nSplit > 0 && nSplit <= 100);
  }
  const handleMaxDesyncValidation = async () => {
    setMaxDesyncValid(validateMaxDesync(maxDesync));
  }
  const validateHtFilter = async () => {
    setHtFilterValid([0, 1, 2, 3].includes(htFilter));
  }
  const validateNSigma = async () => {
    setNSigmaValid( nSigma[0] >= 0 && nSigma[1] >= 0 );
  }
  const validateMEFilter = async () => {
    setMEFilterValid(meFilter >= 0 && meFilter <= 100);
  }

  return (
    <Form dataTa="reconstruction-Form-form" mode="auto">
    <FormItem 
          label="Number of Splits"
          errors={(displayErrors && !nSplitValid) ? "Must be between 1 and 100" : false}
        >            
        <InputField
              min={1}
              max={100}
              autoComplete="off"
              type="number"
              dataTa="splits"
              name="splits"
              value={nSplit}
              onChange={(e) => setNSplit(Math.min(100, Math.max(1, Number(e.target.value))))}
              onBlur={validateNSplit}
              placeholder="Number of splits (1-100)"
              width={Size.S}
            />
        </FormItem>
        <FormItem
            label="Max Desync (s)"
            errors={(displayErrors && !maxDesyncValid) ? "Must be between 0.1 and 10.0 seconds" : false}
          >           
       <InputField
              min={0.1}
              max={60.0}
              step={0.1}
              autoComplete="off"
              type="number"
              dataTa="max_desync"
              name="max_desync"
              value={maxDesync}
              onChange={(e) => setMaxDesync(Number((Math.min(60, Math.max(0.1, Number(e.target.value)))).toFixed(1)))}
              onBlur={handleMaxDesyncValidation}
              placeholder="Max desync (0.1-60.0s)"
              width={Size.S}
            />
        </FormItem>
        <FormItem
            label="HT Filter"
            errors={(displayErrors && !htFilterValid) ? "Must be 0, 1, 2 or 3" : false}
          >      
          <Select width={Size.M} 
           placeholder="Choose filter level"
           style={{ minWidth: '165px' }}  // Added minimum width
           dataTa="scanType" 
           onChange={(value) => {
             setHtFilter(Number(value));
             validateHtFilter();
           }}
           onBlur={validateHtFilter}
           value={htFilter}>
            <Option value={0}>0 - Disabled</Option>
            <Option value={1}>1 - Filter first lidar</Option>
            <Option value={2}>2 - Filter second lidar</Option>
            <Option value={3}>3 - Filter both lidars</Option>
          </Select>
        </FormItem>
        <FormItem
            label="N Sigma (σ, minutes)"
            errors={(displayErrors && !nSigmaValid) ? "STD must be 0-10, Period must be positive" : false}
          >           
           <Stack direction='row'>
              <InputField
                min={0}
                max={10}
                step={1}
                autoComplete="off"
                type="number"
                dataTa="nsigma_std"
                name="nsigma_std"
                value={nSigma[0]}
                onChange={(e) => setNSigma([Math.min(10, Math.max(0, Number(e.target.value))), nSigma[1]])}
                onBlur={validateNSigma}
                placeholder="Std (0-10)"
                width={Size.XS}
              />
              <InputField
                min={0}
                autoComplete="off"
                type="number"
                dataTa="nsigma_period"
                name="nsigma_period"
                value={nSigma[1]}
                onChange={(e) => setNSigma([nSigma[0], Number(e.target.value)])}
                onBlur={validateNSigma}
                placeholder="Period (min)"
                width={Size.XS}
              />
            </Stack>
        </FormItem>
        <FormItem
              label="ME Filter"
              errors={(displayErrors && !meFilterValid) ? "Must be between 0 and 100" : false}
            >          
          <InputField
              min={0}
              max={100}
              step={1}
              autoComplete="off"
              type="number"
              dataTa="me_filter"
              name="me_filter"
              value={meFilter}
              onChange={(e) => setMEFilter(Math.min(100, Math.max(0, Number(e.target.value))))}
              onBlur={validateMEFilter}
              placeholder="0-100%"
              width={Size.S}
            />
        </FormItem>
    </Form>
  );
};